<script>
/**
 * Type   : Layout component
 * Title  : 관리자 레이아웃
 * Author : Aone Logistics
 * Date   : 2023-10-01
 * Version: 1.0.0
 * Description : 관리자 레이아웃 컴포넌트
 * 
 */
import Sidebar from '@/components/admin/common/Sidebar.vue'
import AdminNotificationPanel from '@/components/admin/common/AdminNotificationPanel.vue'
import RegiParcelComp from "@/components/common/RegiParcelComp.vue"
import ParcelExcelUpload from '@/components/common/modal/ParcelExcelUpload.vue'
import Calculator from "@/components/customer/Calculator.vue"
import { latestUpdateVersion } from "@/data/updateReports"
import QRCode from 'qrcode'
import {
  workspaceServices,
  resolveServiceStatus,
  resolveServiceFromPath,
  resolveServiceHome,
  serviceStatusLabel
} from '@/utils/serviceWorkspace'

const ENV_LEVEL = String(process.env.VUE_APP_ENV_LEVEL || '').toUpperCase()
const SHOW_INVENTORY_SERVICE = ['LOCAL', 'DEVELOPMENT'].includes(ENV_LEVEL)
const TOPBAR_SERVICES = workspaceServices.filter(service => (
  service.key !== 'inventory' || SHOW_INVENTORY_SERVICE
))

export default {
  name: 'LayoutAdmin',
  computed: {
    // 로컬 세션 정보
    mobile() {
      return window.innerWidth > 700 ? true : false
    },
    serviceTabs() {
      return TOPBAR_SERVICES.map(service => ({
        ...service,
        status: resolveServiceStatus(this.session, service.key)
      }))
    },
    activeServiceInfo() {
      return this.serviceTabs.find(service => service.key === this.selectedService) || this.serviceTabs[0]
    }
  },
  data(){
    const session = this.$store.getters.getLocalSession
    const routeService = resolveServiceFromPath(this.$route.path)
    const storedService = localStorage.getItem('aoneWorkspaceService')
    const fallbackService = TOPBAR_SERVICES.find(service => (
      resolveServiceStatus(session, service.key) === 'active'
    ))?.key || 'logis'
    const selectedService = routeService || (
      storedService && TOPBAR_SERVICES.some(service => service.key === storedService) &&
        resolveServiceStatus(session, storedService) === 'active'
        ? storedService
        : fallbackService
    )

    return {
      drawer      : window.innerWidth > 700,  // 사이드바 열림 여부
      selectedService,
      serviceAccessDialog: false,
      blockedService: null,

      newParcel   : false,
      calculator  : false,
      uploadExcel : false,
      orderQr     : false,
      notificationDrawer: false,
      version     : latestUpdateVersion,
      adminNotificationEventSource: null,
      notificationRows   : [],
      notificationSummary: {
        unread: 0,
        errors: 0,
        warnings: 0,
        today: 0
      },

      partnerId   : this.$store.getters.getLocalSession.partnerId, // 파트너 ID
      partnerName : this.$store.getters.getLocalSession.partner_name, // 파트너 명
      memberId    : this.$store.getters.getLocalSession.memberId, // 등록자 명
      registerUrl : `${process.env.VUE_APP_API_URL}/parcel-reg-aus-to-kor`, // 주문 등록 URL
      session,
    }
  },
  components:{
    Sidebar,
    AdminNotificationPanel,
    RegiParcelComp,
    ParcelExcelUpload,
    Calculator
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler(path) {
        const routeService = resolveServiceFromPath(path)
        if (routeService) {
          this.selectedService = routeService
          localStorage.setItem('aoneWorkspaceService', routeService)
        }
      }
    }
  },
  methods: {
    serviceStatusLabel,
    openDashboard() {
      if (this.$route.path !== '/admin/dashboard') this.$router.push('/admin/dashboard')
    },
    openUpdateReport() {
      if (this.$route.path !== '/admin/common/update-report') {
        this.$router.push('/admin/common/update-report')
      }
    },
    switchService(service) {
      if (service.status !== 'active') {
        this.blockedService = service
        this.serviceAccessDialog = true
        return
      }

      this.selectedService = service.key
      localStorage.setItem('aoneWorkspaceService', service.key)
      const target = resolveServiceHome(service.key, this.session)
      if (this.$route.path !== target) this.$router.push(target)
    },
    handleQuickAction(action) {
      if (['newParcel', 'calculator', 'uploadExcel', 'orderQr'].includes(action)) {
        this[action] = true
      }
    },
    async signOut() {
      try {
        const res = await this.$http({
          method: 'POST',
          url: '/api/admin/signOut',
          name: '[ Axios / 로그아웃 ]'
        })
        if (res.status !== 200) throw new Error(res.info?.reason || '로그아웃 실패')
        this.$store.dispatch('destroyCustomerInfo')
        sessionStorage.clear()
        this.$router.push('/admin')
      } catch (error) {
        this.$log.debug('/admin/signOut 실패', error)
        this.$q.notify({ type: 'negative', message: '로그아웃 중 오류가 발생했습니다.' })
      }
    },
    getAdminEventSourceUrl() {
      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000'
      return `${baseUrl.replace(/\/$/, '')}/api/events/admin`
    },

    notificationType(severity) {
      const map = {
        error: 'negative',
        warning: 'warning',
        success: 'positive',
        info: 'info'
      }
      return map[severity] || 'info'
    },

    notificationIcon(severity) {
      const map = {
        error: 'error',
        warning: 'warning',
        success: 'check_circle',
        info: 'info'
      }
      return map[severity] || 'notifications'
    },

    async loadAdminNotifications(skipSpinner = false) {
      if (Number(this.session.authLevel) < 90) return

      await this.$http({
        method: 'GET',
        url: '/api/notifications/admin?limit=20',
        name: '[ Axios / 관리자 운영 알림 목록 ]',
        skipSpinner
      }).then((res) => {
        const data = res.data?.data || {}
        this.notificationRows = data.rows || []
        this.notificationSummary = {
          unread: Number(data.summary?.unread || 0),
          errors: Number(data.summary?.errors || 0),
          warnings: Number(data.summary?.warnings || 0),
          today: Number(data.summary?.today || 0)
        }
      }).catch((err) => {
        this.$log.debug('[ AdminLayout / loadAdminNotifications ]', err)
      })
    },

    async markAdminNotificationAsRead(notification) {
      if (!notification?.id) return

      await this.$http({
        method: 'POST',
        url: `/api/notifications/admin/${notification.id}/read`,
        name: '[ Axios / 관리자 운영 알림 읽음 ]',
        skipSpinner: true
      }).then(() => {
        notification.isRead = true
        this.loadAdminNotifications(true)
      }).catch((err) => {
        this.$log.debug('[ AdminLayout / markAdminNotificationAsRead ]', err)
      })
    },

    async markAllAdminNotificationsAsRead() {
      await this.$http({
        method: 'POST',
        url: '/api/notifications/admin/read-all',
        name: '[ Axios / 관리자 운영 알림 전체 읽음 ]',
        skipSpinner: true
      }).then(() => {
        this.loadAdminNotifications(true)
      }).catch((err) => {
        this.$log.debug('[ AdminLayout / markAllAdminNotificationsAsRead ]', err)
      })
    },

    openNotificationTarget(notification) {
      if (!notification?.targetRoute) {
        this.markAdminNotificationAsRead(notification)
        return
      }

      this.markAdminNotificationAsRead(notification)
      this.notificationDrawer = false
      this.$router.push({
        path: notification.targetRoute,
        query: notification.targetPayload || {}
      })
    },

    handleAdminNotification(notification) {
      if (!notification?.id) return

      this.notificationRows = [
        notification,
        ...this.notificationRows.filter(row => row.id !== notification.id)
      ].slice(0, 20)
      this.notificationSummary.unread += 1
      this.notificationSummary.today += 1
      if (notification.severity === 'error') this.notificationSummary.errors += 1
      if (notification.severity === 'warning') this.notificationSummary.warnings += 1

      window.dispatchEvent(new CustomEvent('admin-notification-received', {
        detail: notification
      }))

      this.$q.notify({
        type: this.notificationType(notification.severity),
        icon: this.notificationIcon(notification.severity),
        message: notification.title,
        caption: notification.caption || notification.message || '',
        position: 'top-right',
        timeout: notification.severity === 'error' ? 7000 : 5000,
        actions: [
          {
            label: '확인',
            color: 'white',
            handler: () => this.markAdminNotificationAsRead(notification)
          }
        ]
      })
    },

    connectAdminNotificationEvents() {
      if (Number(this.session.authLevel) < 90 || this.adminNotificationEventSource) return
      if (!window.EventSource) return

      this.adminNotificationEventSource = new EventSource(this.getAdminEventSourceUrl(), { withCredentials: true })
      this.adminNotificationEventSource.addEventListener('notification', (event) => {
        try {
          this.handleAdminNotification(JSON.parse(event.data))
        } catch (error) {
          this.$log.debug('[ AdminLayout / SSE notification parse failed ]', error)
        }
      })
      this.adminNotificationEventSource.onerror = (error) => {
        this.$log.debug('[ AdminLayout / SSE error ]', error)
      }
    },

    disconnectAdminNotificationEvents() {
      if (!this.adminNotificationEventSource) return
      this.adminNotificationEventSource.close()
      this.adminNotificationEventSource = null
    },

    /**
     * Title        : Dialog Show
     * Description  : Dialog가 열릴 때 QR 코드를 생성하는 메소드
     * Status       : Actived
     * 이 메소드는 Dialog가 열릴 때 QR 코드를 생성하여 보여줍니다.
     * QR 코드는 파트너 ID를 포함한 URL을 사용하여 생성됩니다.
     */
    onDialogShow() {
      this.$nextTick(() => {
        const url = `${this.registerUrl}?partnerId=${this.partnerId || '1'}&partnerName=${this.partnerName || 'AONE'}&memberId=${this.memberId}`
        const container = this.$refs.qrContainer
        if (container) {
          container.innerHTML = ''
          QRCode.toCanvas(url, { width: 220 }, (err, canvas) => {
            if (!err && canvas) {
              container.appendChild(canvas)
            }
          })
        }
      })
    }
  },
  async mounted() {
    // 페이지가 로드될 때 사이드바를 열어줌
    this.$log.debug("Admin layout mounted")
    this.connectAdminNotificationEvents()
    this.loadAdminNotifications(true)

    // 관리자 페이지가 로드될 때 로드할 기본적인 목록들을 설정한다
    // await this.$getCodeList("CIT")

    // this.partnerId    = this.session.partnerId
    // this.partnerName  = this.session.partner_name
    // this.memberId     = this.session.memberId
  },
  beforeUnmount() {
    this.disconnectAdminNotificationEvents()
  },
}
</script>

<template>
  <q-layout view="hHh lpR fff">

    <q-header 
      elevated 
      class="bg-cyan-8">
      <q-toolbar class="workspace-toolbar">
        <q-btn flat round dense icon="menu" @click="drawer = !drawer" />

        <div class="workspace-brand q-ml-sm">
          <div class="text-weight-bold">AONE</div>
          <div class="text-caption">{{ Number(session.authLevel) >= 90 ? '관리자' : '파트너' }} 워크스페이스</div>
        </div>

        <q-separator vertical dark class="dashboard-separator q-mx-sm" />
        <q-btn
          flat
          round
          dense
          icon="dashboard"
          class="dashboard-button"
          :class="{ 'dashboard-button--active': $route.path.startsWith('/admin/dashboard') }"
          aria-label="대시보드"
          @click="openDashboard"
        >
          <q-tooltip>대시보드</q-tooltip>
        </q-btn>

        <div class="service-switcher row no-wrap">
          <q-btn
            v-for="service in serviceTabs"
            :key="service.key"
            flat
            no-caps
            class="service-tab q-px-md"
            :class="{
              'service-tab--active': selectedService === service.key,
              'service-tab--blocked': service.status !== 'active'
            }"
            @click="switchService(service)"
          >
            <q-icon :name="service.icon" size="20px" class="q-mr-sm" />
            <span class="service-tab-label">{{ service.label }}</span>
            <q-badge
              v-if="service.status !== 'active'"
              rounded
              color="orange-3"
              text-color="brown-9"
              class="q-ml-sm"
            >
              {{ serviceStatusLabel(service.status) }}
            </q-badge>
          </q-btn>
        </div>

        <q-space />

        <q-btn
          v-if="Number(session.authLevel) >= 90"
          flat
          dense
          no-caps
          class="version-button q-mr-sm"
          icon="new_releases"
          :label="version"
          aria-label="업데이트 리포트"
          @click="openUpdateReport"
        >
          <q-tooltip>업데이트 리포트</q-tooltip>
        </q-btn>

        <q-btn
          v-if="Number(session.authLevel) >= 90"
          outline
          round
          dense
          color="white"
          icon="notifications"
          class="notification-button q-mr-sm"
          @click="notificationDrawer = !notificationDrawer">
          <q-badge v-if="notificationSummary.unread > 0" floating color="red">
            {{ notificationSummary.unread > 99 ? '99+' : notificationSummary.unread }}
          </q-badge>
          <q-tooltip>운영 알림</q-tooltip>
        </q-btn>

      </q-toolbar>
    </q-header>
    

    <!-- // 사이드 바 -->
     <q-drawer
        v-model="drawer"
        show-if-above
        :width="248"
        :breakpoint="700"
        >
        <Sidebar
          :active-service="selectedService"
          @quick-action="handleQuickAction"
          @sign-out="signOut"
        />
      </q-drawer>

      <q-drawer
        v-if="Number(this.session.authLevel) >= 90"
        v-model="notificationDrawer"
        side="right"
        bordered
        :width="420"
        :breakpoint="900"
        class="notification-drawer">
        <AdminNotificationPanel
          :notifications="notificationRows"
          :summary="notificationSummary"
          @close="notificationDrawer = false"
          @refresh="loadAdminNotifications(false)"
          @read="markAdminNotificationAsRead"
          @read-all="markAllAdminNotificationsAsRead"
          @open-target="openNotificationTarget" />
      </q-drawer>
    

    <!----------------------------------------------
    ------------------------------------------------ 
    페이지 router 영역 
    ------------------------------------------------
    ------------------------------------------------>
    <q-page-container>
      <router-view />
    </q-page-container>


    <!-- Parcel registration Modal -->
    <teleport to="body">
      <q-dialog 
        v-model="newParcel"
        :position="'right'"
        full-height>

        <q-card class="card-size full-width">
          <q-toolbar>
            <q-toolbar-title>
              <div class="text-h6">ORDER 등록</div>
            </q-toolbar-title>
            <q-btn flat v-close-popup round dense icon="close" @click="newParcel = false" />
          </q-toolbar>
          <div class="q-pa-md">
            <RegiParcelComp 
              :member-id="String(memberId || '')"
              :partner-id="String(partnerId || '')"
              @close-modal="newParcel = false"
              @refresh-list="this.$router.go(0)"
              />
          </div>
        </q-card>
      </q-dialog>
    </teleport>

    <!-- 무게 계산기 -->
    <teleport to="body">
      <q-dialog 
        v-model="calculator">

        <q-card class="card-size sm-size">
          <q-toolbar>
            <q-toolbar-title>
              <div class="text-h6">무게 계산기</div>
            </q-toolbar-title>
            <q-btn flat v-close-popup round dense icon="close" @click="calculator = !calculator" />
          </q-toolbar>

          <div class="q-pa-md">
            <Calculator />
          </div>
        </q-card>
      </q-dialog>
    </teleport>

    <!-- Excel bulk upload -->
    <teleport to="body">
      <q-dialog 
        v-model="uploadExcel">

        <ParcelExcelUpload 
          @close-modal="uploadExcel = false"/>

      </q-dialog>
    </teleport>


    <!-- QR CODE -->
    <teleport to="body">
      <q-dialog 
        v-model="orderQr" @show="onDialogShow"
        transition-show="slide-up"
        transition-hide="slide-down">

        <q-card class="card-size sm-size">
          <q-toolbar>
            <q-toolbar-title>
              <div class="text-h6">QR로 등록하기</div>
            </q-toolbar-title>
            <q-btn flat v-close-popup round dense icon="close" @click="orderQr = !orderQr" />
          </q-toolbar>

          <div class="q-pa-md">
            <q-card-section class="text-center">
              <div ref="qrContainer"></div>
            </q-card-section>
          </div>
        </q-card>
      
      </q-dialog>
    </teleport>

    <!-- 서비스 사용 등록 안내 -->
    <q-dialog v-model="serviceAccessDialog">
      <q-card style="min-width: 380px; max-width: 520px">
        <q-card-section class="row items-center">
          <q-avatar :icon="blockedService?.icon || 'apps'" color="cyan-8" text-color="white" />
          <div class="q-ml-md">
            <div class="text-h6">{{ blockedService?.title }}</div>
            <div class="text-caption text-grey-7">현재 상태: {{ serviceStatusLabel(blockedService?.status) }}</div>
          </div>
        </q-card-section>
        <q-card-section>
          <template v-if="blockedService?.status === 'pending'">
            사용 등록 요청을 검토 중입니다. 승인 후 이 워크스페이스를 사용할 수 있습니다.
          </template>
          <template v-else-if="blockedService?.status === 'suspended'">
            서비스 이용이 정지된 상태입니다. 담당 관리자에게 문의해 주세요.
          </template>
          <template v-else>
            이 서비스를 사용하려면 파트너별 사용 등록이 필요합니다. 관리자에게 등록을 요청해 주세요.
          </template>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="확인" color="cyan-9" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>

  </q-layout>
</template>

<style scoped lang="scss">
.workspace-toolbar {
  position: relative;
  min-height: 58px;
}

.service-switcher {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.workspace-brand {
  min-width: 116px;
  line-height: 1.15;
}

.dashboard-separator {
  align-self: center;
  height: 28px;
  opacity: 0.5;
}

.dashboard-button {
  background: rgba(255, 255, 255, 0.08);
}

.dashboard-button--active {
  background: rgba(255, 255, 255, 0.24);
}

.version-button {
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 14px;
  padding: 2px 10px;
}

.service-tab {
  min-height: 58px;
  border-radius: 0;
  border-bottom: 3px solid transparent;
}

.service-tab--active {
  background: rgba(255, 255, 255, 0.16);
  border-bottom-color: #fff;
}

.service-tab--blocked {
  opacity: 0.78;
}

@media (max-width: 900px) {
  .workspace-brand,
  .service-tab-label,
  .service-tab .q-badge {
    display: none;
  }

  .service-tab {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
}

.rotate {
  transition: 0.5s ease-in-out;
  transform:rotate(0deg);
}

.opened {
  transition: 0.5s ease-in-out;
  transform:rotate(405deg)
}

.hover {
  &:hover {
    cursor: pointer;
  }
}

.notification-button {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.75);
}

.card-size {

  .sm-size {
    width: 400px;
    max-width: 80vw;
  }

  .full-width {
    width: 100%;
    min-width: 320px;
    max-width: 1200px;
  }


}
</style>
